import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";

interface TourSpotlightProps {
  /**
   * CSS selector for the element to spotlight. If omitted or not found, the
   * screen is fully dimmed and the popup is centered — no cutout.
   */
  targetSelector?: string;
  /** Padding around the target rect for the spotlight cutout. */
  padding?: number;
  /** Border radius for the spotlight cutout. */
  radius?: number;
  /** Popup content (message + action buttons). */
  children: ReactNode;
  /** Where to place the popup relative to the target. */
  popupPreferredPlacement?: "auto" | "top" | "bottom";
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const OVERLAY_COLOR = "rgba(0, 0, 0, 0.55)";
const POPUP_GAP = 16;
const POPUP_MARGIN = 16;
const POPUP_MAX_WIDTH = 360;

/**
 * Full-screen dim overlay with an optional cutout over a target element.
 * Clicks on the four surrounding dim panels are absorbed (nothing else on
 * the page is interactive during the tour); clicks INSIDE the cutout pass
 * through to the underlying element so the user can tap the real button.
 */
export default function TourSpotlight({
  targetSelector,
  padding = 8,
  radius = 12,
  children,
  popupPreferredPlacement = "auto",
}: TourSpotlightProps) {
  const [rect, setRect] = useState<Rect | null>(null);
  const [viewport, setViewport] = useState(() => ({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  }));
  const rafRef = useRef<number | null>(null);

  // Continuously track the target rect while the tour is active. rAF keeps
  // the spotlight glued to a moving element (scroll, resize, animations)
  // without needing separate scroll/resize/mutation observers.
  useLayoutEffect(() => {
    if (!targetSelector) {
      setRect(null);
      return;
    }

    // Scroll the target to center the FIRST time we find it. `block: 'center'`
    // centers in the nearest scrollable ancestor (AppLayout's overflow-y-auto
    // div, not window) which reliably clears both the fixed top header and
    // bottom nav that flank the scrollport. Unconditional on purpose: an
    // earlier "only scroll when not fully visible" guard used
    // window.innerHeight which incorrectly included the nav's area, so the
    // Share button on /qr passed the visibility check while actually sitting
    // behind the bottom nav. Fixed-position elements (the nav / header
    // buttons the other tracks target) don't move under scrollIntoView, so
    // making this unconditional costs them nothing.
    let hasScrolled = false;

    const tick = () => {
      const el = document.querySelector(targetSelector) as HTMLElement | null;
      if (el) {
        if (!hasScrolled) {
          hasScrolled = true;
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
        const r = el.getBoundingClientRect();
        setRect((prev) => {
          if (
            prev &&
            prev.top === r.top &&
            prev.left === r.left &&
            prev.width === r.width &&
            prev.height === r.height
          ) {
            return prev;
          }
          return { top: r.top, left: r.left, width: r.width, height: r.height };
        });
      } else {
        setRect(null);
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [targetSelector]);

  useEffect(() => {
    const onResize = () =>
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Expanded rect (target + padding, clamped to viewport).
  const cutout = rect
    ? {
        top: Math.max(0, rect.top - padding),
        left: Math.max(0, rect.left - padding),
        width: Math.min(viewport.width, rect.width + padding * 2),
        height: Math.min(viewport.height, rect.height + padding * 2),
      }
    : null;

  // Decide popup placement — above or below the target, whichever has more
  // room. Falls back to a small stagger when the target is off-screen.
  let popupStyle: React.CSSProperties;
  if (cutout) {
    const spaceAbove = cutout.top;
    const spaceBelow = viewport.height - (cutout.top + cutout.height);
    const placeAbove =
      popupPreferredPlacement === "top" ||
      (popupPreferredPlacement === "auto" && spaceAbove > spaceBelow);
    const centerLeft = cutout.left + cutout.width / 2;
    const clampedLeft = Math.min(
      Math.max(centerLeft - POPUP_MAX_WIDTH / 2, POPUP_MARGIN),
      viewport.width - POPUP_MAX_WIDTH - POPUP_MARGIN
    );
    popupStyle = placeAbove
      ? {
          position: "fixed",
          bottom: viewport.height - cutout.top + POPUP_GAP,
          left: clampedLeft,
          width: POPUP_MAX_WIDTH,
          maxWidth: `calc(100vw - ${POPUP_MARGIN * 2}px)`,
        }
      : {
          position: "fixed",
          top: cutout.top + cutout.height + POPUP_GAP,
          left: clampedLeft,
          width: POPUP_MAX_WIDTH,
          maxWidth: `calc(100vw - ${POPUP_MARGIN * 2}px)`,
        };
  } else {
    popupStyle = {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: POPUP_MAX_WIDTH,
      maxWidth: `calc(100vw - ${POPUP_MARGIN * 2}px)`,
    };
  }

  return (
    <>
      {cutout ? (
        <>
          {/* Visual dim: a viewport-sized shadow projected off the cutout div's
              border. Because border-radius shapes the shadow's inner edge, the
              dim overlay curves around the same corners as the highlight ring —
              no hard-square corners peeking out. pointer-events: none so this
              layer never intercepts clicks; the invisible blockers below do. */}
          <div
            style={{
              position: "fixed",
              top: cutout.top,
              left: cutout.left,
              width: cutout.width,
              height: cutout.height,
              borderRadius: radius,
              boxShadow: `0 0 0 100vmax ${OVERLAY_COLOR}`,
              pointerEvents: "none",
              zIndex: 60,
            }}
          />
          {/* Four transparent panels around the cutout's bounding rect that
              absorb clicks so nothing on the page is interactive except the
              highlighted target. */}
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              height: cutout.top,
              zIndex: 61,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: cutout.top,
              left: 0,
              width: cutout.left,
              height: cutout.height,
              zIndex: 61,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: cutout.top,
              left: cutout.left + cutout.width,
              right: 0,
              height: cutout.height,
              zIndex: 61,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: cutout.top + cutout.height,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 61,
            }}
          />
          {/* Highlight ring around the target. pointer-events: none so clicks
              still pass through to the real element beneath. */}
          <div
            style={{
              position: "fixed",
              top: cutout.top,
              left: cutout.left,
              width: cutout.width,
              height: cutout.height,
              borderRadius: radius,
              boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.9), 0 0 24px rgba(59, 130, 246, 0.5)",
              pointerEvents: "none",
              zIndex: 62,
            }}
          />
        </>
      ) : (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: OVERLAY_COLOR,
            zIndex: 60,
          }}
        />
      )}

      <div style={{ ...popupStyle, zIndex: 63 }} className="pointer-events-auto">
        {children}
      </div>
    </>
  );
}
